import { Link } from '@tanstack/react-router'
import { 
  Palette, 
  Type, 
  Square, 
  Layout, 
  Navigation,
  CreditCard as CardIcon,
  MousePointer,
  Grid3X3,
  Settings,
  Briefcase,
  Sparkles,
  Bell,
  BarChart3,
  PuzzleIcon,
  FileText,
  Eye,
  Heart
} from 'lucide-react'

const DesignSystemPage = () => {
  const sections = [
    {
      title: 'Fundamentos',
      description: 'Cores, tipografia e elementos básicos do sistema',
      items: [
        {
          title: 'Cores',
          description: 'Paleta de cores do MonuTrack',
          icon: Palette,
          path: '/design-system/cores',
          color: 'bg-brand-primary'
        },
        {
          title: 'Tipografia',
          description: 'Fontes Monumenta e estilos de texto',
          icon: Type,
          path: '/design-system/tipografia',
          color: 'bg-brand-blue'
        },
        {
          title: 'Marca & Recursos',
          description: 'Logo, brand voice e assets da Monumenta',
          icon: Briefcase,
          path: '/design-system/brand-assets',
          color: 'bg-brand-accent-600'
        }
      ]
    },
    {
      title: 'Componentes Básicos',
      description: 'Elementos fundamentais da interface',
      items: [
        {
          title: 'Botões',
          description: 'Diferentes variações de botões',
          icon: MousePointer,
          path: '/design-system/botoes',
          color: 'bg-brand-red'
        },
        {
          title: 'Cards',
          description: 'Componentes de cartão e containers',
          icon: CardIcon,
          path: '/design-system/cards',
          color: 'bg-success'
        },
        {
          title: 'Formulários',
          description: 'Inputs, selects e elementos de formulário',
          icon: Settings,
          path: '/design-system/formularios',
          color: 'bg-warning'
        }
      ]
    },
    {
      title: 'Layout & Navegação',
      description: 'Estrutura e organização da interface',
      items: [
        {
          title: 'Grid & Layout',
          description: 'Sistema de grid e layout responsivo',
          icon: Grid3X3,
          path: '/design-system/layout',
          color: 'bg-info'
        },
        {
          title: 'Navegação',
          description: 'Menus, breadcrumbs e elementos de navegação',
          icon: Navigation,
          path: '/design-system/navegacao',
          color: 'bg-brand-gray'
        },
        {
          title: 'Padrões de Layout',
          description: 'Templates e estruturas de página',
          icon: Layout,
          path: '/design-system/layout-patterns',
          color: 'bg-brand-accent-700'
        }
      ]
    },
    {
      title: 'Interações & Feedback',
      description: 'Animações, transições e comunicação com usuário',
      items: [
        {
          title: 'Interações & Animações',
          description: 'Hover states, transições e micro-interações',
          icon: Sparkles,
          path: '/design-system/interactions-animations',
          color: 'bg-brand-primary'
        },
        {
          title: 'Feedback & Status',
          description: 'Alerts, notificações e estados do sistema',
          icon: Bell,
          path: '/design-system/feedback-status',
          color: 'bg-brand-blue'
        }
      ]
    },
    {
      title: 'Componentes Específicos',
      description: 'Componentes únicos e avançados do MonuTrack',
      items: [
        {
          title: 'Métricas',
          description: 'Cards de métricas e dashboards',
          icon: Square,
          path: '/design-system/metricas',
          color: 'bg-brand-accent-600'
        },
        {
          title: 'Componentes Financeiros',
          description: 'Componentes específicos da área financeira',
          icon: CardIcon,
          path: '/design-system/financeiros',
          color: 'bg-brand-accent-700'
        },
        {
          title: 'Visualização de Dados',
          description: 'Gráficos, tabelas e componentes de dados',
          icon: BarChart3,
          path: '/design-system/data-visualization',
          color: 'bg-success'
        },
        {
          title: 'Analytics Dashboard',
          description: 'Dashboard interativo de performance com métricas temporais',
          icon: BarChart3,
          path: '/design-system/analytics-dashboard',
          color: 'bg-brand-primary'
        },
        {
          title: 'Componentes Avançados',
          description: 'Modais, tooltips e padrões complexos',
          icon: PuzzleIcon,
          path: '/design-system/advanced-components',
          color: 'bg-warning'
        }
      ]
    },
    {
      title: 'Diretrizes & Padrões',
      description: 'Guias de conteúdo, acessibilidade e melhores práticas',
      items: [
        {
          title: 'Diretrizes de Conteúdo',
          description: 'Tom de voz, escrita e padrões de conteúdo',
          icon: FileText,
          path: '/design-system/content-guidelines',
          color: 'bg-brand-red'
        },
        {
          title: 'Acessibilidade',
          description: 'Princípios e práticas de design inclusivo',
          icon: Eye,
          path: '/design-system/accessibility',
          color: 'bg-info'
        }
      ]
    }
  ]

  return (
    <div className="min-h-screen bg-brand-dark text-white">
      {/* Header */}
      <div className="bg-brand-dark-light border-b border-brand-gray/20">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="text-center">
            <h1 className="text-4xl font-display font-bold text-brand-primary mb-4">
              Sistema de Design MonuTrack
            </h1>
            <p className="text-xl text-brand-gray max-w-3xl mx-auto">
              Explore os componentes, padrões e diretrizes que compõem a identidade visual e experiência do MonuTrack
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        {sections.map((section, sectionIndex) => (
          <div key={sectionIndex} className="mb-16">
            <div className="mb-8">
              <h2 className="text-3xl font-display font-semibold text-white mb-3">
                {section.title}
              </h2>
              <p className="text-lg text-brand-gray">
                {section.description}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {section.items.map((item, itemIndex) => {
                const IconComponent = item.icon
                
                return (
                  <Link
                    key={itemIndex}
                    to={item.path}
                    className="group bg-brand-dark-light rounded-monumenta p-6 hover:bg-brand-dark-lighter transition-all duration-200 border border-brand-gray/10 hover:border-brand-primary/30 hover:shadow-monumenta-brand"
                  >
                    <div className="flex items-start space-x-4">
                      <div className={`p-3 rounded-monumenta ${item.color} text-white group-hover:scale-110 transition-transform duration-200`}>
                        <IconComponent size={24} />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-display font-semibold text-white group-hover:text-brand-primary transition-colors duration-200 mb-2">
                          {item.title}
                        </h3>
                        <p className="text-brand-gray group-hover:text-white transition-colors duration-200">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  </Link>
                )
              })}
            </div>
          </div>
        ))}

        {/* Footer Info */}
        <div className="mt-20 text-center">
          <div className="bg-brand-dark-light rounded-monumenta p-8 border border-brand-gray/10">
            <h3 className="text-2xl font-display font-semibold text-brand-primary mb-4">
              Sobre este Sistema
            </h3>
            <p className="text-brand-gray max-w-2xl mx-auto leading-relaxed">
              Este sistema de design foi desenvolvido para manter a consistência visual e melhorar a experiência 
              do usuário em todo o ecossistema MonuTrack. Cada componente segue as diretrizes da marca Monumenta 
              e foi otimizado para performance e acessibilidade.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DesignSystemPage
